import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getClaimableCount, fetchAvailableForPlayer } from '@/api/quests';
import { useAuthStore } from '@/stores/auth.store';
import { useSessionStore } from '@/stores/session.store';
import { AUTH_TOKEN_KEY } from '@/api/client';

const SEEN_AVAILABLE_KEY = 'theho.quests.seenAvailable';

/**
 * Compteur de quetes "Recuperer" (claimable) -- alimente le badge sur
 * les icones Quetes (HeaderGlobalNav + MobileBottomNav).
 *
 * Poll toutes les 90s -- les quetes evoluent au rythme des ticks, pas
 * besoin d'un refresh agressif. Le QuestsView appelle refresh() apres
 * un completeQuest pour decrement immediat du badge.
 *
 * Le compteur est par player-universe : on lit session.activePlayerUniverseId
 * (≠ auth.user.id qui est l'id du COMPTE joueur, pas de la partie).
 */
const POLL_INTERVAL_MS = 90_000;

export const useQuestsStore = defineStore('quests', () => {
  const claimableCount = ref(0);
  // Nb de quêtes DISPONIBLES (acceptables) + seuil déjà « vu » (localStorage)
  // pour l'indicateur passif « nouvelle quête » du menu.
  const availableCount = ref(0);
  const seenAvailable = ref<number>(Number(localStorage.getItem(SEEN_AVAILABLE_KEY) ?? '0') || 0);
  const loading = ref(false);
  let inFlight: Promise<void> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const auth = useAuthStore();
  const session = useSessionStore();

  /** Une nouvelle quête est dispo depuis la dernière consultation du menu. */
  const hasNewQuests = computed(() => availableCount.value > seenAvailable.value);

  /** Marque les quêtes dispo comme vues (à l'ouverture de la modale Quêtes). */
  function acknowledgeAvailable(): void {
    seenAvailable.value = availableCount.value;
    try { localStorage.setItem(SEEN_AVAILABLE_KEY, String(seenAvailable.value)); } catch { /* ignore */ }
  }

  async function refresh(): Promise<void> {
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
      claimableCount.value = 0;
      return;
    }
    if (!auth.user || !session.inUniverse) {
      claimableCount.value = 0;
      return;
    }
    if (inFlight) return inFlight;
    loading.value = true;
    inFlight = (async () => {
      try {
        const puId = session.activePlayerUniverseId ?? 0;
        claimableCount.value = await getClaimableCount(puId);
        const avail = await fetchAvailableForPlayer(puId);
        availableCount.value = avail.length;
        // Si des quêtes dispo ont été acceptées/expirées, on ramène le seuil vu
        // sous le total courant (sinon l'indicateur ne réapparaîtrait jamais).
        if (seenAvailable.value > availableCount.value) {
          seenAvailable.value = availableCount.value;
          try { localStorage.setItem(SEEN_AVAILABLE_KEY, String(seenAvailable.value)); } catch { /* ignore */ }
        }
      } catch {
        // Silencieux : un fail de count ne doit pas spammer l'utilisateur.
      } finally {
        loading.value = false;
        inFlight = null;
      }
    })();
    return inFlight;
  }

  function start(): void {
    if (pollTimer) return;
    void refresh();
    pollTimer = setInterval(refresh, POLL_INTERVAL_MS);
  }

  function stop(): void {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function clear(): void {
    claimableCount.value = 0;
    availableCount.value = 0;
  }

  return {
    claimableCount, availableCount, hasNewQuests, loading,
    refresh, acknowledgeAvailable, start, stop, clear,
  };
});
